import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';

import { SbSharedContentModuleLandingVideo } from '../SbSharedContentModuleLandingVideo/SbSharedContentModuleLandingVideo.tsx';
import { FullPageWidth } from '../../components/global/FullPageWidth/FullPageWidth.tsx';
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';

import { type SbRespContentModuleLanding_browseContentModule$key } from './__generated__/SbRespContentModuleLanding_browseContentModule.graphql.ts';
import { type SbRespContentModuleLanding_viewer$key } from './__generated__/SbRespContentModuleLanding_viewer.graphql.ts';

import styles from './SbRespContentModuleLanding.module.css';
import dibsCss from 'dibs-css';
import cn from 'classnames';

type Props = {
    browseContentModule: SbRespContentModuleLanding_browseContentModule$key;
    viewer: SbRespContentModuleLanding_viewer$key | null | undefined;
    hidePhoto: boolean;
    className?: string;
};

export const SbRespContentModuleLanding: FC<Props> = ({
    browseContentModule: browseContentModuleRef,
    viewer: viewerRef,
}) => {
    const browseContentModule = useFragment(
        graphql`
            fragment SbRespContentModuleLanding_browseContentModule on ContentModuleLanding {
                bannerVideo
                isMuxVideo
                bannerImage
                eyeBrowText
                landingBackgroundTintColor
                landingTilesBackgroundColor
                landingTextColor
                title
                landingTiles {
                    title
                    imageUrl
                    linkData {
                        ...SeoLink_linkData
                    }
                    localizedLinkDataList {
                        currency
                        linkData {
                            ...SeoLink_linkData
                        }
                    }
                }
            }
        `,
        browseContentModuleRef
    );

    const viewer = useFragment(
        graphql`
            fragment SbRespContentModuleLanding_viewer on Viewer {
                ...SbSharedContentModuleLandingVideo_viewer
            }
        `,
        viewerRef
    );

    const { currency } = useCurrency();

    const {
        title,
        landingTiles,
        bannerVideo,
        eyeBrowText,
        landingBackgroundTintColor,
        landingTilesBackgroundColor,
        landingTextColor,
        bannerImage,
        isMuxVideo,
    } = browseContentModule || {};

    return (
        <FullPageWidth className={dibsCss.mbMedium}>
            <div
                className={cn(
                    dibsCss.wFull,
                    dibsCss.relative,
                    dibsCss.overflowHidden,
                    styles.container
                )}
            >
                <SbSharedContentModuleLandingVideo
                    width={1920}
                    height={1080}
                    id={bannerVideo || ''}
                    title={title || ''}
                    thumbnailUrl={
                        isMuxVideo
                            ? bannerImage || ''
                            : `${bannerImage}?disable=upscale&auto=webp&quality=60` || ''
                    }
                    isMuxVideo={!!isMuxVideo}
                    viewer={viewer}
                />

                <div
                    style={{
                        backgroundColor: `${landingBackgroundTintColor}B3`,
                        color: landingTextColor || '#ffffff',
                    }}
                    className={cn(
                        dibsCss.top0,
                        dibsCss.left0,
                        dibsCss.hFull,
                        dibsCss.wFull,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.absolute,
                        dibsCss.textCenter,
                        dibsCss.gapSmaller,
                        dibsCss.itemsCenter,
                        dibsCss.justifyCenter
                    )}
                >
                    <span className={dibsCss.sassyFontHeaderLargeItalic}>{eyeBrowText}</span>
                    <span
                        className={cn(
                            dibsCss.textCenter,
                            dibsCss.sassyFontHeaderXxLarge,
                            styles.collection
                        )}
                    >
                        {title}
                    </span>
                </div>
            </div>
            <div
                style={{
                    backgroundColor: landingTilesBackgroundColor || '#ffffff',
                }}
                className={cn(
                    dibsCss.flex,
                    dibsCss.wFull,
                    dibsCss.pyLarge,
                    dibsCss.pxLarger,
                    dibsCss.gapSmaller,
                    dibsCss.sassyFontHeaderSmall
                )}
            >
                {landingTiles?.map((tile, index) => {
                    const {
                        title: tileTitle,
                        imageUrl,
                        linkData: tileLinkData,
                        localizedLinkDataList,
                    } = tile || {};
                    const localizedLinkData = localizedLinkDataList?.find(
                        item => item?.currency === currency
                    );
                    const linkData = localizedLinkData?.linkData || tileLinkData;
                    return tileTitle && imageUrl && linkData ? (
                        <SeoLink
                            key={index}
                            linkData={linkData}
                            className={cn(
                                dibsCss.flex1,
                                dibsCss.relative,
                                dibsCss.overflowHidden,
                                dibsCss.roundedXxsmall,
                                styles.tile
                            )}
                        >
                            <img
                                alt={tileTitle}
                                src={imageUrl}
                                srcSet={getSrcsetString(imageUrl || '', [800])}
                                sizes="130px"
                                className={cn(dibsCss.objectCover, dibsCss.hFull, dibsCss.wFull)}
                            />
                            <span
                                style={{
                                    background: `linear-gradient(#32150700 0%, ${landingBackgroundTintColor} 100%)`,
                                    color: landingTextColor || '#ffffff',
                                }}
                                className={cn(
                                    dibsCss.flex,
                                    dibsCss.wFull,
                                    dibsCss.left0,
                                    dibsCss.pSmall,
                                    dibsCss.bottom0,
                                    dibsCss.itemsEnd,
                                    dibsCss.absolute,
                                    dibsCss.h50,
                                    styles.tileTitle
                                )}
                            >
                                {tileTitle}
                            </span>
                        </SeoLink>
                    ) : null;
                })}
            </div>
            {/* using a div with an id for anc</FullPageWidth>ng on a collection link */}
            <div id="grid-results" />
        </FullPageWidth>
    );
};
